import {useMemo} from 'react';

import {useHelpLink} from '../../provider/help-links/help-links-hook';
import {
    LEGAL_DOCUMENTATION_CONFIG_LINK_KEY,
    parseLegalDocumentationHelpLinkConfig,
} from '../../provider/help-links/legal-documentation-help-link-config';
import {useLocale} from '../../provider/locale/locale-hook';
import {Locale} from '../../provider/locale/localization';

import {
    resolvePrivacyPolicyDocumentationHref,
    resolveTermsDocumentationHref,
} from './legal-agreement-policy-documentation-hrefs';
import {LegalAgreementTypeEnum} from './legal-agreement-type';

/**
 * Checkbox acceptance line (privacy + optional public-offer links).
 * URLs come from `config` on the legal-documentation help_links row when set; otherwise
 * {@link policyLinksByDomain} per domain/locale (with RocketData fallback for unknown domains).
 */
export function useAgreementAcceptanceContent(legalAgreementType?: LegalAgreementTypeEnum): JSX.Element | null {
    const {shortLocaleName} = useLocale();
    const configLink = useHelpLink(LEGAL_DOCUMENTATION_CONFIG_LINK_KEY, true);

    const config = useMemo(() => parseLegalDocumentationHelpLinkConfig(configLink?.config), [configLink?.config]);

    const privacyHref = resolvePrivacyPolicyDocumentationHref(config, shortLocaleName);
    const termsHref = resolveTermsDocumentationHref(config, shortLocaleName);

    return useMemo(() => {
        if (!legalAgreementType) {
            return null;
        }

        const privacyPolicyLink = (
            <a href={privacyHref} rel="noreferrer" target="_blank">
                <Locale stringKey="LEGAL_AGREEMENT__PRIVACY_POLICY_LINK" />
            </a>
        );

        const publicAgreementLink = (
            <a href={termsHref} rel="noreferrer" target="_blank">
                <Locale stringKey="LEGAL_AGREEMENT__PUBLIC_AGREEMENT_LINK" />
            </a>
        );

        const textsByAgreementType = {
            [LegalAgreementTypeEnum.Personal]: (
                <Locale stringKey="LEGAL_AGREEMENT__PERSONAL__DESCRIPTION" valueMap={{privacyPolicyLink}} />
            ),
            [LegalAgreementTypeEnum.Public]: (
                <Locale
                    stringKey="LEGAL_AGREEMENT__PUBLIC__DESCRIPTION"
                    valueMap={{privacyPolicyLink, publicAgreementLink}}
                />
            ),
        };

        return textsByAgreementType[legalAgreementType];
    }, [legalAgreementType, privacyHref, termsHref]);
}
